import React, { useEffect, useState } from 'react';
import { Save, RotateCcw, User } from 'lucide-react';
import FormSection from './shared/FormSection';
import JSONImporter from './shared/JSONImporter';
import { CreatorAuthority, CreatorRole, ImportResult } from '../types';
import { validateCreatorAuthority } from '../schemas';
// Import the catalogService - adjust path based on your file structure
import { catalogService } from './services/catalogService';

export interface CreatorAuthorityFormProps {
  initialCreator?: Partial<CreatorAuthority>;
  onSave?: (saved: CreatorAuthority) => void | Promise<void>;
}

const CreatorAuthorityForm: React.FC<CreatorAuthorityFormProps> = ({ initialCreator, onSave }) => {
  const [formData, setFormData] = useState<Partial<CreatorAuthority>>({
    recordType: 'authority',
    recordStatus: 'draft',
    preferredName: '',
    nameVariants: [],
    datesOfActivity: '',
    birthDate: '',
    deathDate: '',
    primaryRole: undefined,
    additionalRoles: [],
    nationality: [],
    placeOfBirth: '',
    placeOfDeath: '',
    biography: '',
    schoolsAffiliation: [],
    workshopsAffiliation: [],
    patronage: [],
    viafId: '',
    lcnafId: '',
    wikidata: '',
    relatedCreators: [],
    worksCreated: [],
    additionalImages: [],
    notes: '',
    sources: []
  });

  const [savedMessage, setSavedMessage] = useState('');
  const [showImporter, setShowImporter] = useState(false);

  useEffect(() => {
    if (initialCreator && Object.keys(initialCreator).length > 0) {
      setFormData(prev => ({ ...prev, ...initialCreator }));
    }
  }, [initialCreator]);

  const roles: CreatorRole[] = [
    'Author', 'Writer', 'Scribe', 'Copyist', 'Artist', 'Illuminator',
    'Illustrator', 'Printer', 'Publisher', 'Binder', 'Engraver',
    'Etcher', 'Cartographer', 'Editor', 'Translator', 'Patron',
    'Former Owner', 'Other'
  ];

  const handleChange = (field: keyof CreatorAuthority, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayInput = (field: keyof CreatorAuthority, value: string) => {
    const array = value.split('\n').map(item => item.trim()).filter(Boolean);
    handleChange(field, array);
  };

  // Import handler that updates form state
  const handleImport = async (data: any[]): Promise<ImportResult> => {
    try {
      // Take the first record from the array
      const importedData = data[0];
      
      console.log('🔥 Importing creator data:', importedData);
      
      // Generate ID if not present
      const creatorId = importedData.id || `creator-${Date.now()}`;
      
      // Merge imported data with form, ensuring all arrays exist
      const mergedData: Partial<CreatorAuthority> = {
        ...formData,
        ...importedData,
        
        // Ensure arrays are properly initialized
        nameVariants: importedData.nameVariants || [],
        additionalRoles: importedData.additionalRoles || [],
        nationality: importedData.nationality || [],
        schoolsAffiliation: importedData.schoolsAffiliation || [],
        workshopsAffiliation: importedData.workshopsAffiliation || [],
        patronage: importedData.patronage || [],
        relatedCreators: importedData.relatedCreators || [],
        worksCreated: importedData.worksCreated || [],
        additionalImages: importedData.additionalImages || [],
        sources: importedData.sources || [],
        
        // Use provided ID or generate new one
        id: creatorId,
        
        // Set timestamps
        createdDate: importedData.createdDate || new Date().toISOString(),
        lastModified: new Date().toISOString()
      };
      
      console.log('✅ Creator import successful, data ready:', mergedData);
      
      return {
        success: true,
        recordsImported: 1,
        recordsFailed: 0,
        errors: [],
        importedIds: [creatorId]
      };
      
    } catch (error) {
      console.error('❌ Import failed:', error);
      return {
        success: false,
        recordsImported: 0,
        recordsFailed: 1,
        errors: [error instanceof Error ? error.message : 'Import failed'],
        importedIds: []
      };
    }
  };

  // This is called by JSONImporter after successful import
  const handleImportSuccess = (importedDataArray: any[]) => {
    if (!importedDataArray || importedDataArray.length === 0) {
      console.warn('⚠️ No data to import');
      return;
    }

    const importedData = importedDataArray[0];
    console.log('🔥 handleImportSuccess called with:', importedData);
    
    // Merge imported data with form, ensuring all arrays exist
    const mergedData: Partial<CreatorAuthority> = {
      ...formData,
      ...importedData,
      
      // Ensure arrays are properly initialized
      nameVariants: importedData.nameVariants || [],
      additionalRoles: importedData.additionalRoles || [],
      nationality: importedData.nationality || [],
      schoolsAffiliation: importedData.schoolsAffiliation || [],
      workshopsAffiliation: importedData.workshopsAffiliation || [],
      patronage: importedData.patronage || [],
      relatedCreators: importedData.relatedCreators || [],
      worksCreated: importedData.worksCreated || [],
      additionalImages: importedData.additionalImages || [],
      sources: importedData.sources || [],
      
      // Generate ID if not present
      id: importedData.id || `creator-${Date.now()}`,
      
      // Set timestamps
      createdDate: importedData.createdDate || new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
    
    // UPDATE THE FORM STATE
    console.log('📝 Updating form with merged data:', mergedData);
    setFormData(mergedData);
    
    // Hide the importer
    setShowImporter(false);
    
    // Show success message
    setSavedMessage('✓ JSON imported successfully! Review and save when ready.');
    setTimeout(() => setSavedMessage(''), 5000);
    
    // Scroll to top so user can see populated fields
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    console.log('✅ Form populated with imported data');
  };

  const handleSubmit = async () => {
    const validation = validateCreatorAuthority(formData);
    
    if (!validation.isValid) {
      setSavedMessage('❌ ' + validation.errors.join('; '));
      setTimeout(() => setSavedMessage(''), 5000);
      return;
    }
    
    try {
      const completeData: CreatorAuthority = {
        id: formData.id || `creator-${Date.now()}`,
        recordType: 'authority',
        recordStatus: formData.recordStatus || 'draft',
        createdDate: formData.createdDate || new Date().toISOString(),
        lastModified: new Date().toISOString(),
        preferredName: formData.preferredName || '',
        nameVariants: formData.nameVariants || [],
        datesOfActivity: formData.datesOfActivity,
        birthDate: formData.birthDate,
        deathDate: formData.deathDate,
        primaryRole: formData.primaryRole!,
        additionalRoles: formData.additionalRoles,
        nationality: formData.nationality,
        placeOfBirth: formData.placeOfBirth,
        placeOfDeath: formData.placeOfDeath,
        biography: formData.biography,
        schoolsAffiliation: formData.schoolsAffiliation,
        workshopsAffiliation: formData.workshopsAffiliation,
        patronage: formData.patronage,
        viafId: formData.viafId,
        lcnafId: formData.lcnafId,
        wikidata: formData.wikidata,
        relatedCreators: formData.relatedCreators,
        worksCreated: formData.worksCreated,
        additionalImages: formData.additionalImages,
        notes: formData.notes,
        sources: formData.sources
      };

      console.log('💾 Saving creator to Firebase...', completeData);
      
      // Save to Firebase using catalogService
      const savedId = await catalogService.saveCreator(completeData);
      
      console.log('✅ Creator saved with ID:', savedId);

      // Call the optional callback if provided
      if (onSave) {
        await onSave(completeData);
      }
      
      setSavedMessage('✓ Creator record saved successfully to Firebase!');
      setTimeout(() => setSavedMessage(''), 3000);
      
    } catch (error) {
      console.error('❌ Save error:', error);
      setSavedMessage('❌ Failed to save: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setTimeout(() => setSavedMessage(''), 5000);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all fields to initial values?')) {
      setFormData(initialCreator || {
        recordType: 'authority',
        recordStatus: 'draft',
        preferredName: '',
        nameVariants: [],
        primaryRole: undefined,
        additionalRoles: [],
        nationality: [],
        sources: []
      });
    }
  };

  const exampleJSON = {
    recordType: 'authority',
    recordStatus: 'draft',
    preferredName: 'Schedel, Hartmann',
    nameVariants: ['Hartmann Schedel', 'Hartmannus Schedel'],
    datesOfActivity: '1440-1514',
    birthDate: '1440',
    deathDate: '1514',
    primaryRole: 'Author',
    additionalRoles: ['Physician', 'Humanist'],
    nationality: ['German'],
    placeOfBirth: 'Nuremberg',
    placeOfDeath: 'Nuremberg',
    biography: 'German physician, humanist, and historian...',
    schoolsAffiliation: [],
    workshopsAffiliation: [],
    patronage: [],
    viafId: '100161068',
    lcnafId: '',
    wikidata: '',
    relatedCreators: [],
    worksCreated: [],
    additionalImages: [],
    notes: '',
    sources: []
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <header className="flex items-center justify-between pb-4 border-b">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <User className="text-amber-600" size={28} />
            Creator Authority Record
          </h1>
          <p className="text-sm text-gray-600 mt-1">Following VIAF/LC NAF Authority Standards</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReset} className="px-4 py-2 border rounded hover:bg-gray-50">
            <RotateCcw size={16} className="inline mr-2" />
            Reset
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700">
            <Save size={16} className="inline mr-2" />
            Save to Firebase
          </button>
        </div>
      </header>

      {savedMessage && (
        <div className={`p-4 rounded-lg border ${
          savedMessage.includes('✓') 
            ? 'bg-green-100 border-green-400 text-green-800' 
            : 'bg-red-100 border-red-400 text-red-800'
        }`}>
          {savedMessage}
        </div>
      )}

      {/* JSON Import */}
      {showImporter && (
        <div className="mb-6">
          <JSONImporter
            entityType="creator"
            onImport={handleImport}
            onImportSuccess={handleImportSuccess}
            validateFn={validateCreatorAuthority}
            exampleJSON={exampleJSON}
          />
        </div>
      )}

      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowImporter(!showImporter)}
          className="text-sm text-amber-700 hover:text-amber-900 underline"
        >
          {showImporter ? 'Hide' : 'Show'} JSON Import
        </button>
      </div>

      {/* Basic Information */}
      <FormSection
        title="Basic Information"
        description="Primary name and role information"
        required={true}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Preferred Name * <span className="text-red-600">Required</span>
            </label>
            <input
              type="text"
              value={formData.preferredName || ''}
              onChange={(e) => handleChange('preferredName', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Last name, First name (birth-death)"
            />
            <p className="text-xs text-gray-500 mt-1">Format: "Schedel, Hartmann (1440-1514)"</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Name Variants
            </label>
            <textarea
              value={(formData.nameVariants || []).join('\n')}
              onChange={(e) => handleArrayInput('nameVariants', e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
              placeholder="Alternative names (one per line)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Primary Role * <span className="text-red-600">Required</span>
            </label>
            <select
              value={formData.primaryRole || ''}
              onChange={(e) => handleChange('primaryRole', e.target.value as CreatorRole)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select primary role...</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Additional Roles
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {roles.map(role => (
                <label key={role} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={(formData.additionalRoles || []).includes(role)}
                    onChange={(e) => {
                      const current = formData.additionalRoles || [];
                      if (e.target.checked) {
                        handleChange('additionalRoles', [...current, role]);
                      } else {
                        handleChange('additionalRoles', current.filter(r => r !== role));
                      }
                    }}
                  />
                  <span className="text-sm">{role}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </FormSection>

      {/* Dates */}
      <FormSection title="Dates and Activity">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Birth Date</label>
            <input
              type="text"
              value={formData.birthDate || ''}
              onChange={(e) => handleChange('birthDate', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="YYYY or YYYY-MM-DD"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Death Date</label>
            <input
              type="text"
              value={formData.deathDate || ''}
              onChange={(e) => handleChange('deathDate', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="YYYY or YYYY-MM-DD"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Dates of Activity</label>
            <input
              type="text"
              value={formData.datesOfActivity || ''}
              onChange={(e) => handleChange('datesOfActivity', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="e.g., 1440-1514"
            />
          </div>
        </div>
      </FormSection>

      {/* Geographic */}
      <FormSection title="Geographic Information">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nationality</label>
            <input
              type="text"
              value={(formData.nationality || []).join(', ')}
              onChange={(e) => {
                const array = e.target.value.split(',').map(n => n.trim()).filter(Boolean);
                handleChange('nationality', array);
              }}
              className="w-full border rounded px-3 py-2"
              placeholder="E.g., German, Italian (separated by commas)"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Place of Birth</label>
              <input
                type="text"
                value={formData.placeOfBirth || ''}
                onChange={(e) => handleChange('placeOfBirth', e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="City, Country"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Place of Death</label>
              <input
                type="text"
                value={formData.placeOfDeath || ''}
                onChange={(e) => handleChange('placeOfDeath', e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="City, Country"
              />
            </div>
          </div>
        </div>
      </FormSection>

      {/* Biography */}
      <FormSection title="Biographical Information">
        <div>
          <label className="block text-sm font-medium mb-1">Biography</label>
          <textarea
            value={formData.biography || ''}
            onChange={(e) => handleChange('biography', e.target.value)}
            className="w-full border rounded px-3 py-2"
            rows={8}
            placeholder="Biographical narrative..."
          />
        </div>
      </FormSection>

      {/* Professional Context */}
      <FormSection title="Professional Context">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Schools Affiliation</label>
            <textarea
              value={(formData.schoolsAffiliation || []).join('\n')}
              onChange={(e) => handleArrayInput('schoolsAffiliation', e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
              placeholder="Universities, schools (one per line)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Workshops Affiliation</label>
            <textarea
              value={(formData.workshopsAffiliation || []).join('\n')}
              onChange={(e) => handleArrayInput('workshopsAffiliation', e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
              placeholder="Workshops, studios (one per line)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Patronage</label>
            <textarea
              value={(formData.patronage || []).join('\n')}
              onChange={(e) => handleArrayInput('patronage', e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
              placeholder="Patrons (one per line)"
            />
          </div>
        </div>
      </FormSection>

      {/* External Identifiers */}
      <FormSection title="External Identifiers">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">VIAF ID</label>
            <input
              type="text"
              value={formData.viafId || ''}
              onChange={(e) => handleChange('viafId', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Virtual International Authority File ID"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">LC NAF ID</label>
            <input
              type="text"
              value={formData.lcnafId || ''}
              onChange={(e) => handleChange('lcnafId', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Library of Congress Name Authority File"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Wikidata ID</label>
            <input
              type="text"
              value={formData.wikidata || ''}
              onChange={(e) => handleChange('wikidata', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Wikidata identifier"
            />
          </div>
        </div>
      </FormSection>

      {/* Notes and Sources */}
      <FormSection title="Notes and Sources">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={4}
              placeholder="Additional notes or observations..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Sources</label>
            <textarea
              value={(formData.sources || []).join('\n')}
              onChange={(e) => handleArrayInput('sources', e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={4}
              placeholder="Bibliographic sources (one per line)"
            />
          </div>
        </div>
      </FormSection>

      {/* Debug view */}
      <details className="p-4 bg-gray-100 rounded">
        <summary className="cursor-pointer font-semibold">
          Debug: Current Form Data
        </summary>
        <pre className="mt-2 text-xs overflow-auto max-h-96">
          {JSON.stringify(formData, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default CreatorAuthorityForm;